import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  Promotion,
  PromotionProductRule,
  AdvancedConfig,
  RecurringConfig,
} from './usePromotions';

// ---- Types ----

export interface CartItem {
  id: string;
  product_id: string;
  name: string;
  price: number; // effective unit price
  base_price: number;
  quantity: number;
  category_id?: string | null;
  supplier_id?: string | null;
  sku?: string | null;
}

export interface ItemDiscount {
  promotionId: string;
  promotionName: string;
  discount: number; // total discount for this item (quantity * per-unit-discount)
  perUnitDiscount: number;
}

export interface PromotionResult {
  itemDiscounts: Map<string, ItemDiscount>; // key = cart item id
  totalDiscount: number;
  appliedPromotions: Array<{
    id: string;
    name: string;
    totalDiscount: number;
  }>;
}

// ---- Active Promotions Query ----

/**
 * Fetch all currently active promotions for a given location.
 * Includes product rules for matching.
 */
export function useActivePromotions(locationId: string | null) {
  return useQuery({
    queryKey: ['active-promotions', locationId],
    queryFn: async () => {
      if (!locationId) return [];

      const now = new Date().toISOString();

      // Get promotion IDs active at this location
      const { data: locPromos } = await supabase
        .from('promotion_locations')
        .select('promotion_id')
        .eq('location_id', locationId);

      if (!locPromos || locPromos.length === 0) return [];

      const promoIds = locPromos.map((lp: any) => lp.promotion_id);

      // Fetch the promotions with their rules
      const { data, error } = await supabase
        .from('promotions')
        .select(`
          *,
          promotion_product_rules(*),
          promotion_locations(*)
        `)
        .in('id', promoIds)
        .eq('is_active', true)
        .or(`start_datetime.is.null,start_datetime.lte.${now}`)
        .or(`end_datetime.is.null,end_datetime.gte.${now}`)
        .order('priority', { ascending: true });

      if (error) throw error;

      // Filter recurring promotions by current day/time
      const filtered = (data || []).filter((promo: any) => {
        if (promo.schedule_type !== 'recurring') return true;
        return isRecurringActive(promo.recurring_config as RecurringConfig);
      });

      return filtered as Promotion[];
    },
    enabled: !!locationId,
    staleTime: 1000 * 60, // Re-check every minute
    refetchInterval: 1000 * 60, // Auto-refetch every minute for time-based promotions
  });
}

// ---- Promotion Calculation Engine ----

/**
 * Check if a recurring promotion is currently active based on day and time.
 */
function isRecurringActive(config: RecurringConfig | null): boolean {
  if (!config) return false;

  const now = new Date();
  const currentDay = now.getDay(); // 0=Sunday

  if (!config.days.includes(currentDay)) return false;

  if (config.all_day) return true;

  if (config.start_time && config.end_time) {
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    return currentTime >= config.start_time && currentTime <= config.end_time;
  }

  return true;
}

/**
 * Check if a product matches a set of product rules (inclusion rules).
 */
function productMatchesRules(
  item: CartItem,
  rules: PromotionProductRule[],
  ruleGroupPrefix: string
): boolean {
  const includeRules = rules.filter(r => r.rule_group === `${ruleGroupPrefix}_include`);
  const excludeRules = rules.filter(r => r.rule_group === `${ruleGroupPrefix}_exclude`);

  // If no include rules, the product matches (scope is 'all')
  if (includeRules.length === 0) return true;

  // Check if product matches any include rule
  const included = includeRules.some(rule => {
    switch (rule.filter_type) {
      case 'product':
        return item.product_id === rule.filter_value;
      case 'category':
        return item.category_id === rule.filter_value;
      case 'supplier':
        return item.supplier_id === rule.filter_value;
      case 'sku':
        return item.sku === rule.filter_value;
      default:
        return false;
    }
  });

  if (!included) return false;

  // Check exclusion rules
  const excluded = excludeRules.some(rule => {
    switch (rule.filter_type) {
      case 'product':
        return item.product_id === rule.filter_value;
      case 'category':
        return item.category_id === rule.filter_value;
      case 'supplier':
        return item.supplier_id === rule.filter_value;
      case 'sku':
        return item.sku === rule.filter_value;
      default:
        return false;
    }
  });

  return !excluded;
}

/**
 * Calculate the per-unit discount for a basic promotion on a given item.
 */
function calculateBasicDiscount(
  item: CartItem,
  promo: Promotion
): number {
  if (promo.discount_type === 'percentage' && promo.discount_value) {
    return item.price * (promo.discount_value / 100);
  }
  if (promo.discount_type === 'fixed' && promo.discount_value) {
    return Math.min(promo.discount_value, item.price);
  }
  return 0;
}

/**
 * Calculate promotion discounts for a set of cart items.
 *
 * Conflict resolution (Lightspeed rules):
 * - For each item, the best deal (highest discount) wins.
 * - If two promotions give the same discount, the one with lower priority (created first) wins.
 */
export function calculatePromotionDiscounts(
  cartItems: CartItem[],
  promotions: Promotion[]
): PromotionResult {
  const itemDiscounts = new Map<string, ItemDiscount>();
  const promotionTotals = new Map<string, number>();

  if (!promotions || promotions.length === 0 || !cartItems || cartItems.length === 0) {
    return { itemDiscounts, totalDiscount: 0, appliedPromotions: [] };
  }

  // Process basic promotions
  const basicPromos = promotions.filter(p => p.promotion_type === 'basic');
  const advancedPromos = promotions.filter(p => p.promotion_type === 'advanced');

  // For each cart item, find the best basic promotion
  for (const item of cartItems) {
    let bestDiscount = 0;
    let bestPromo: Promotion | null = null;

    for (const promo of basicPromos) {
      const rules = promo.promotion_product_rules || [];

      // Check if this item qualifies
      const qualifies = promo.product_scope === 'all'
        ? !rules.some(r => r.rule_group === 'basic_exclude' && productMatchesRules(item, [r], 'basic'))
        : productMatchesRules(item, rules, 'basic');

      if (!qualifies) continue;

      const discount = calculateBasicDiscount(item, promo);

      if (discount > bestDiscount || (discount === bestDiscount && bestPromo && promo.priority < bestPromo.priority)) {
        bestDiscount = discount;
        bestPromo = promo;
      }
    }

    if (bestPromo && bestDiscount > 0) {
      const totalItemDiscount = bestDiscount * item.quantity;
      itemDiscounts.set(item.id, {
        promotionId: bestPromo.id,
        promotionName: bestPromo.name,
        discount: totalItemDiscount,
        perUnitDiscount: bestDiscount,
      });

      const prev = promotionTotals.get(bestPromo.id) || 0;
      promotionTotals.set(bestPromo.id, prev + totalItemDiscount);
    }
  }

  // Process advanced promotions (buy X get Y)
  for (const promo of advancedPromos) {
    const config = promo.advanced_config as AdvancedConfig | null;
    if (!config) continue;

    const rules = promo.promotion_product_rules || [];
    const buyCondition = config.buy_condition;
    const getReward = config.get_reward;

    if (buyCondition.trigger === 'buys_items') {
      // Find qualifying "buy" items
      const buyItems = cartItems.filter(item => {
        if (buyCondition.product_scope === 'all') return true;
        return productMatchesRules(item, rules, 'buy');
      });

      const totalBuyQty = buyItems.reduce((sum, item) => sum + item.quantity, 0);
      const requiredQty = buyCondition.quantity || 1;

      if (totalBuyQty < requiredQty) continue;

      // Find qualifying "get" items
      const getItems = cartItems.filter(item => {
        if (getReward.product_scope === 'all') return true;
        return productMatchesRules(item, rules, 'get');
      });

      if (getItems.length === 0) continue;

      // Apply reward to get items (limited by reward quantity)
      const rewardQty = getReward.quantity || 1;
      let appliedQty = 0;

      // Sort get items by price ascending (cheapest first for free/discount)
      const sortedGetItems = [...getItems].sort((a, b) => a.price - b.price);

      for (const item of sortedGetItems) {
        if (appliedQty >= rewardQty) break;

        const qtyToApply = Math.min(item.quantity, rewardQty - appliedQty);
        let perUnitDiscount = 0;

        if (getReward.reward_type === 'free') {
          perUnitDiscount = item.price;
        } else if (getReward.reward_type === 'percentage' && getReward.discount_value) {
          perUnitDiscount = item.price * (getReward.discount_value / 100);
        } else if (getReward.reward_type === 'fixed' && getReward.discount_value) {
          perUnitDiscount = Math.min(getReward.discount_value, item.price);
        }

        if (perUnitDiscount > 0) {
          // Check if there's already a better discount for this item
          const existing = itemDiscounts.get(item.id);
          const newTotalDiscount = perUnitDiscount * qtyToApply;

          if (!existing || newTotalDiscount > existing.discount) {
            // Remove previous promotion's total if replacing
            if (existing) {
              const prevTotal = promotionTotals.get(existing.promotionId) || 0;
              promotionTotals.set(existing.promotionId, prevTotal - existing.discount);
            }

            itemDiscounts.set(item.id, {
              promotionId: promo.id,
              promotionName: promo.name,
              discount: newTotalDiscount,
              perUnitDiscount,
            });

            const prev = promotionTotals.get(promo.id) || 0;
            promotionTotals.set(promo.id, prev + newTotalDiscount);
          }
        }

        appliedQty += qtyToApply;
      }
    } else if (buyCondition.trigger === 'spends_amount') {
      // Check if total spend meets threshold
      const cartTotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const threshold = buyCondition.spend_amount || 0;

      if (cartTotal < threshold) continue;

      // Apply reward to get items
      const getItems = cartItems.filter(item => {
        if (getReward.product_scope === 'all') return true;
        return productMatchesRules(item, rules, 'get');
      });

      const rewardQty = getReward.quantity || 1;
      let appliedQty = 0;

      const sortedGetItems = [...getItems].sort((a, b) => a.price - b.price);

      for (const item of sortedGetItems) {
        if (appliedQty >= rewardQty) break;

        const qtyToApply = Math.min(item.quantity, rewardQty - appliedQty);
        let perUnitDiscount = 0;

        if (getReward.reward_type === 'free') {
          perUnitDiscount = item.price;
        } else if (getReward.reward_type === 'percentage' && getReward.discount_value) {
          perUnitDiscount = item.price * (getReward.discount_value / 100);
        } else if (getReward.reward_type === 'fixed' && getReward.discount_value) {
          perUnitDiscount = Math.min(getReward.discount_value, item.price);
        }

        if (perUnitDiscount > 0) {
          const existing = itemDiscounts.get(item.id);
          const newTotalDiscount = perUnitDiscount * qtyToApply;

          if (!existing || newTotalDiscount > existing.discount) {
            if (existing) {
              const prevTotal = promotionTotals.get(existing.promotionId) || 0;
              promotionTotals.set(existing.promotionId, prevTotal - existing.discount);
            }

            itemDiscounts.set(item.id, {
              promotionId: promo.id,
              promotionName: promo.name,
              discount: newTotalDiscount,
              perUnitDiscount,
            });

            const prev = promotionTotals.get(promo.id) || 0;
            promotionTotals.set(promo.id, prev + newTotalDiscount);
          }
        }

        appliedQty += qtyToApply;
      }
    }
  }

  // Build totals
  let totalDiscount = 0;
  const appliedPromotions: Array<{ id: string; name: string; totalDiscount: number }> = [];

  for (const [promoId, total] of promotionTotals) {
    if (total > 0) {
      const promo = promotions.find(p => p.id === promoId);
      totalDiscount += total;
      appliedPromotions.push({
        id: promoId,
        name: promo?.name || 'Unknown',
        totalDiscount: total,
      });
    }
  }

  return { itemDiscounts, totalDiscount, appliedPromotions };
}
